/**
 * LinkPickers.js
 * Инлайн-пикеры привязки расхода к Объекту/Контакту: свёрнутая кнопка
 * «Привязать …», разворачивается в поиск + список. Выбор → onPick(item).
 * Список приходит из props уже загруженным (клиентская фильтрация по имени/
 * адресу) — без серверного поиска, как и раньше в ExpenseFab.
 *
 * Раньше PropertyPickerInline жил только в ExpenseFab.js (создание расхода
 * с Дашборда без контекста); теперь тем же пикером пользуется и редактирование
 * расхода (ExpensesPage.js), плюс новый ClientPickerInline — там раньше
 * привязку к Контакту нельзя было ни задать, ни сменить руками.
 */
import React, { useMemo, useState } from 'react';

function EntityPickerInline({ items, onPick, t, icon, attachLabel, searchPlaceholder, emptyLabel, filterFn, renderItem }) {
  const [expanded, setExpanded] = useState(false);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(it => filterFn(it, needle));
  }, [items, q, filterFn]);

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 12px',
          borderRadius: 20,
          background: 'var(--bg3)',
          border: '1px dashed var(--accent, #6366f1)',
          fontSize: 13, fontWeight: 500,
          color: 'var(--accent, #6366f1)',
          cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span>{attachLabel}</span>
      </button>
    );
  }

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 12,
      background: 'var(--bg2)',
      overflow: 'hidden',
      maxWidth: 420,
    }}>
      <input
        autoFocus
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder={searchPlaceholder}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '9px 12px',
          border: 'none', borderBottom: '1px solid var(--border)',
          background: 'transparent',
          color: 'var(--text)', fontSize: 14, outline: 'none',
        }}
      />
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '12px', fontSize: 13, color: 'var(--text3)' }}>
            {emptyLabel}
          </div>
        ) : filtered.map(it => (
          <button
            key={it.id}
            type="button"
            onClick={() => onPick(it)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '9px 12px',
              border: 'none', borderBottom: '1px solid var(--border)',
              background: 'transparent', cursor: 'pointer',
              color: 'var(--text)',
            }}
          >
            {renderItem(it)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PropertyPickerInline({ properties, onPick, t }) {
  return (
    <EntityPickerInline
      items={properties}
      onPick={onPick}
      t={t}
      icon="📍"
      attachLabel={t('expenses.attachProperty')}
      searchPlaceholder={t('expenses.searchProperty')}
      emptyLabel={t('expenses.noProperties')}
      filterFn={(p, needle) =>
        (p.name || '').toLowerCase().includes(needle) ||
        (p.address || '').toLowerCase().includes(needle)
      }
      renderItem={p => (
        <>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{p.name}</div>
          {p.address && (
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{p.address}</div>
          )}
        </>
      )}
    />
  );
}

export function ClientPickerInline({ clients, onPick, t }) {
  return (
    <EntityPickerInline
      items={clients}
      onPick={onPick}
      t={t}
      icon="👤"
      attachLabel={t('expenses.attachClient')}
      searchPlaceholder={t('expenses.searchClient')}
      emptyLabel={t('expenses.noClients')}
      filterFn={(c, needle) =>
        (c.name || '').toLowerCase().includes(needle) ||
        (c.phone || '').toLowerCase().includes(needle) ||
        (c.email || '').toLowerCase().includes(needle)
      }
      renderItem={c => (
        <>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{c.name}</div>
          {(c.phone || c.email) && (
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
              {[c.phone, c.email].filter(Boolean).join(' · ')}
            </div>
          )}
        </>
      )}
    />
  );
}
